import {
    Card,
    CardActions,
    CardContent,
    CardMedia,
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import * as _ from 'lodash';
import { debounce } from 'lodash';
import { inject } from 'mobx-react';
import * as React from 'react';
import Button from 'src/common/Button/Button';
import { DefaultAttachmentFile, IObservation } from 'src/Definitions';
import { AttachmentFileDto, ObservationAttachmentFileDto } from 'src/generated_client';
import { IStores } from 'src/Stores';
import uuidv4 from 'uuid/v4';

const styles = () =>
    createStyles({
        buttons: {
            'margin-top': 100,
        },
        fileInput: { display: 'none' },
        media: {
            height: 200,
            width: 200,
        },
    });

interface ICarouselProps extends WithStyles<typeof styles> {
    observation?: IObservation;
    closeCarousel?: () => void;
    isCarouselOpen?: boolean;
    attachmentFiles?: ObservationAttachmentFileDto[];
}

class Carousel extends React.Component<ICarouselProps> {
    public state = {
        attachmentFiles: this.props.attachmentFiles,
        index: 0,
        isFileLoading: false,
    };
    public upload: HTMLInputElement | null;

    public get selectedPicture() {
        if (this.state.attachmentFiles) {
            return this.state.attachmentFiles[this.state.index];
        } else {
            return new ObservationAttachmentFileDto({ attachmentFile: new AttachmentFileDto() });
        }
    }

    private getAttachmentFileUrl(attachmentFile: ObservationAttachmentFileDto) {
        if (attachmentFile && attachmentFile.attachmentFile) {
            return attachmentFile.attachmentFile.url;
        } else {
            return '';
        }
    }

    public handleOnButtonClick = (target: Carousel) => {
        return () => target.upload && target.upload.click();
    };

    public handleFileChange = () => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
                _.forEach(event.target.files, (file) => {
                    const reader = new FileReader();
                    reader.onloadstart = () => {
                        this.setState({
                            isFileLoading: true,
                        });
                    };
                    const newAttachmentFiles = this.state.attachmentFiles;
                    reader.onload = () => {
                        if (reader) {
                            const newAttachmentFile = new AttachmentFileDto({
                                ...DefaultAttachmentFile(),
                                content: (reader.result! as string).split(',')[1],
                                mimeType: file.type.split('/')[1],
                                name: file.name,
                                url: reader.result as string,
                            });

                            const newObservationAttachmentFile = new ObservationAttachmentFileDto({
                                attachmentFile: newAttachmentFile,
                                attachmentFileId: newAttachmentFile.id,
                                id: uuidv4(),
                                isActive: true,
                                name: this.props.observation!.name,
                                observationId: this.props.observation!.id,
                            });
                            newAttachmentFiles!.push(newObservationAttachmentFile);
                            this.props.observation!.isModified = true;
                            this.setState({
                                attachmentFiles: newAttachmentFiles,
                                isFileLoading: false,
                            });
                        }
                    };
                    reader.readAsDataURL(file);
                });
            }
        };
    };

    private incrementCarouselWindow = () => {
        let newState = this.state.index + 1;
        if (this.state.index === this.props.attachmentFiles!.length - 1) {
            newState = 0;
        }
        this.setState({
            index: newState,
        });
    };

    private decrementCarouselWindow = () => {
        let newState = this.state.index - 1;
        if (this.state.index === 0) {
            newState = this.props.attachmentFiles!.length - 1;
        }
        this.setState({
            index: newState,
        });
    };

    private removePicture = debounce(() => {
        const newAttachmentFiles = this.state.attachmentFiles;
        newAttachmentFiles!.splice(this.state.index, 1);
        this.setState({
            attachmentFiles: newAttachmentFiles,
        });
    });

    public openFileLink = (observationAttachmentFileDto: ObservationAttachmentFileDto) => () => {
        window.open(observationAttachmentFileDto.attachmentFile!.url, '_blank');
    };

    public render() {
        const { isCarouselOpen, closeCarousel, classes } = this.props;
        return (
            <Dialog open={isCarouselOpen || true} maxWidth="lg" onClose={closeCarousel}>
                <DialogTitle>Attachments</DialogTitle>
                <DialogContent>{this.renderImages()}</DialogContent>
                <DialogActions>
                    <Button
                        component="label"
                        isLoading={this.state.isFileLoading}
                        onClick={this.handleOnButtonClick(this)}
                        variant="contained"
                        color="primary"
                    >
                        Add File
                    </Button>
                    <input
                        type="file"
                        className={classes.fileInput}
                        onChange={this.handleFileChange()}
                        ref={(ref) => (this.upload = ref)}
                        style={{ display: 'none' }}
                        multiple
                    />
                    <Button onClick={closeCarousel} data-cy="close">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    public renderImages() {
        if (this.selectedPicture) {
            return (
                <Grid container spacing={8}>
                    <Grid item xs={2}>
                        <IconButton
                            aria-label="Rotate Left"
                            color="primary"
                            onClick={this.decrementCarouselWindow}
                            className={this.props.classes.buttons}
                        >
                            <KeyboardArrowLeft />
                        </IconButton>
                    </Grid>
                    <Grid item xs={8}>
                        <Card>
                            <CardMedia
                                style={{ cursor: 'pointer' }}
                                onClick={this.openFileLink(this.selectedPicture)}
                                image={this.getAttachmentFileUrl(this.selectedPicture)}
                                className={this.props.classes.media}
                            />
                            <CardContent>
                                <Typography variant="body2" color="textSecondary" component="p">
                                    {this.selectedPicture.attachmentFile!.name}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Button onClick={this.removePicture} size="small" color="primary">
                                    Delete
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                    <Grid item xs={2}>
                        <IconButton
                            aria-label="Rotate Right"
                            color="primary"
                            onClick={this.incrementCarouselWindow}
                            className={this.props.classes.buttons}
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </Grid>
                </Grid>
            );
        } else {
            return '';
        }
    }
}

const InjectedCarousel = inject<IStores, ICarouselProps, Partial<ICarouselProps>, any>(
    (stores: IStores) => ({
        attachmentFiles: stores.observations.carouselAttachmentFiles,
        closeCarousel: stores.observations.toggleCarousel,
        isCarouselOpen: stores.observations.isCarouselOpen,
        observation: stores.observations.selectedObservation,
    })
)(withStyles(styles)(Carousel));

export default InjectedCarousel;



// WEBPACK FOOTER //
// ./src/chevrons/behaviors/observations/Carousel.tsx